import React, { useState } from 'react';
import { businessApi } from '@/lib/api/business';
import { useTheme } from '@/context/ThemeContext';
import { useBusinessWorkspace } from './BusinessWorkspaceContext';
import { useBusinessLimitRequests } from './useBusinessLimitRequests';
import { Action, ActionState, Field, ListState } from './BusinessControls.web';
import { creditNumber, dateTime, statusLabel } from './businessSettingsHelpers';
import { businessAppTheme } from './businessAppTheme';
import './businessSettings.css';

export default function BusinessLimitRequests({ api = businessApi, workspaceId, mineOnly = false, changed }) {
  const { rows, amount, setAmount, reason, setReason, action, submit, decide, disabled } = useBusinessLimitRequests({ api, workspaceId, mineOnly, changed });
  return <section className="gb-stack" aria-label="Credit limit requests">
    <h3>Credit limit requests</h3>
    <p>Requests raise a member’s monthly usage limit. Owners and authorized admins review them; approval uses the company’s existing shared credits.</p>
    {rows.currentLimitCredits != null && <p>Your current limit: <strong>{creditNumber(rows.currentLimitCredits)} credits / month</strong></p>}
    {rows.canRequest && <form className="gb-stack" onSubmit={submit}>
      <Field label="Requested monthly limit"><input aria-label="Requested monthly limit" inputMode="numeric" maxLength={12} required disabled={disabled} value={amount} onChange={event => setAmount(event.target.value)} /></Field>
      <Field label="Reason"><textarea aria-label="Reason" maxLength={500} required disabled={disabled} value={reason} onChange={event => setReason(event.target.value)} /></Field>
      <div><button type="submit" disabled={disabled || !amount.trim() || !reason.trim()}>Send limit request</button></div>
    </form>}
    {rows.pendingRequestId && <p>Your request is awaiting review. You can withdraw it before sending another.</p>}
    <ActionState action={action} />
    {rows.items.map(row => <article className="gb-card gb-stack" key={row.id} aria-label={`Limit request from ${row.mine ? 'you' : row.name}`}>
      <div><strong>{row.mine ? 'Your request' : row.name}</strong> · {statusLabel(row.status)}</div>
      <p>{creditNumber(row.previousLimitCredits)} → {creditNumber(row.requestedLimitCredits)} credits / month</p>
      <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{row.reason}</p>
      <small>{dateTime(row.createdAt)}{row.status === 'pending' ? ` · Expires ${dateTime(row.expiresAt)}` : ''}</small>
      {row.stale && <p>This member’s limit or access changed. A new request is needed.</p>}
      <div className="gb-row">
        {!mineOnly && row.canApprove && <Action disabled={disabled} onClick={() => decide(row, 'approve')}>Approve request</Action>}
        {!mineOnly && row.canDeny && <Action disabled={disabled} onClick={() => decide(row, 'deny')}>Deny request</Action>}
        {row.canWithdraw && <Action disabled={disabled} onClick={() => decide(row, 'withdraw')}>Withdraw request</Action>}
      </div>
    </article>)}
    <ListState resource={rows} empty="No limit requests yet." />
    <div><Action disabled={disabled} onClick={rows.refresh}>Refresh requests</Action></div>
  </section>;
}

export function BusinessLimitRequestPanel({ workspace, balance }) {
  const [open, setOpen] = useState(false), context = useBusinessWorkspace(), { colors, theme } = useTheme();
  if (!workspace.membership || workspace.membership.role === 'owner' || balance.memberLimitCredits == null) return null;
  return <div className="gb-business" style={{ ...businessAppTheme(colors, theme), padding: '8px 16px' }}>
    <div><Action aria-expanded={open} onClick={() => setOpen(value => !value)}>{open ? 'Hide credit limit requests' : 'Request a higher credit limit'}</Action></div>
    {open && <BusinessLimitRequests api={context?.api || businessApi} workspaceId={workspace.id} mineOnly />}
  </div>;
}
